<template>
  <teleport to="body">
    <div class="modal">
      <div
        ref="modalLayout"
        :class="{ 'modal__layout': true, 'modal__layout--overflow': modalOverflow}"
      >
        <h1 class="modal__title typo-ignore typo-title">
          <span>{{ title }}</span>
          <button
            :class="{ 'modal__close': true, 'button--disabled': saving }"
            type="button"
            @click="cancel"
          >
            <UseSvg
              class="icon icon--small"
              name="close_16"
            />
          </button>
        </h1>

        <div class="modal__content">
          <AlertBar
            v-if="errors?.error_count > 0"
            :modifiers="['flat', 'light', 'padding']"
          >
            {{ $t('tasks.error_message') }}
          </AlertBar>

          <div class="form__group">
            <FormRow
              :label="$t('tasks.description')"
              :labelFor="`task_${task.id}_description`"
              :modifiers="['narrow']"
            >
              <div class="form-row__input">
                <div
                  :class="{
                    'input input--textarea': true,
                    'input--error': errors?.task?.description,
                  }"
                >
                  <textarea
                    :id="`task_${task.id}_description`"
                    v-model="task.description"
                    :placeholder="$t('tasks.description_placeholder')"
                    rows="6"
                    @keyup="clearValidationMessage('description')"
                  />
                </div>
              </div>
            </FormRow>

            <FormRow
              :label="$t('tasks.type')"
              :labelFor="`task_${task.id}_type`"
              :modifiers="[
                'margin',
                'lean',
                'lateral',
                !errors?.task?.task_type && 'narrow',
                errors?.task?.task_type && 'margin-xy',
                errors?.task?.task_type && 'error-bg',
              ]"
            >
              <div class="form-row__input">
                <Select
                  v-model="task.task_type"
                  :items="taskTypeOptions"
                  :placeholder="$t('tasks.unspecified')"
                  @update:model-value="clearValidationMessage('task_type')"
                />
              </div>
            </FormRow>
          </div>

          <div class="form__group">
            <FormRow
              :label="$t('tasks.due_date')"
              :modifiers="['margin', 'narrow', 'lean', 'lateral']"
              :optional="true"
            >
              <div class="form-row__input">
                <DatePicker
                  v-model="task.due_date"
                  :canClear="true"
                  :minDate="new Date(inspectionDate)"
                  :placeholder="$t('tasks.no_due_date')"
                />
              </div>
            </FormRow>
          </div>

          <div class="form__group">
            <FormRow
              :label="$t('tasks.labels')"
              :modifiers="['margin', 'narrow', 'lean']"
              :optional="true"
            >
              <div class="form-row__input">
                <LabelInput
                  v-model="task.labels"
                  :options="allLabels"
                  :placeholder="$t('tasks.no_labels')"
                />
              </div>
            </FormRow>
          </div>

          <div class="form__group">
            <FormRow
              :label="$t('tasks.work_order')"
              :labelFor="`task_${task.id}_external_reference`"
              :modifiers="['margin', 'narrow', 'lean']"
              :optional="true"
            >
              <div class="form-row__input">
                <div class="input">
                  <input
                    :id="`task_${task.id}_external_reference`"
                    v-model="task.external_reference"
                    type="text"
                  >
                </div>
              </div>
            </FormRow>
          </div>
          <div class="form__group">
            <div class="form-row form-row--margin">
              <div class="issue__section issue__section--light">
                <AttachmentUploader
                  v-model:documentList="task.task_documents"
                  @delete-file="removeTaskAttachment"
                  @update-blob="saveBlob($event, 'attachment')"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="modal__actions">
          <button
            :class="{ 'button': true, 'button--disabled': saving }"
            type="button"
            @click="cancel"
          >
            {{ $t('general.cancel') }}
          </button>
          <FormSubmitButton
            :buttonLabel="saveButtonText"
            :isSaving="saving"
            @click="save"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, reactive, computed, onMounted, inject, watch, nextTick } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';

import UseSvg from '@/components/generic/use_svg.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import DatePicker from '@/components/form/date_picker.vue';
import LabelInput from '@/components/form/label_input.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import AlertBar from '@/components/form/alert_bar.vue';
import AttachmentUploader from '@/components/generic/attachment_uploader.vue';

import useAjax from '@/functions/use_ajax';
import { DirectUpload } from '@rails/activestorage';
import useEmitter from '@/functions/use_emitter';

const props = defineProps({
  initialTask: { type: Object, required: true },
  issue: { type: Object, default: null },
  inspectionDate: { type: String, default: null },
  errorSaving: { type: Boolean, required: false, default: false },
});
const emit = defineEmits(['cancel', 'submit', 'delete']);

const taskTypeOptions = [
  'protection_housekeeping',
  'immediate_securing_action',
  'repair_replace',
  'scoping_job',
].map((type) => ({
  value: type,
  label: I18n.t(`tasks.type_names.${type}`),
}));

const uploadUrl = inject('uploadUrl');
const route = useRoute();
const ajax = useAjax();
const emitter = useEmitter();

const existingLabels = ref([]);
const saving = ref(false);
const modalLayout = ref(null);
const modalOverflow = ref(false);

const dialogIsOpen = ref(false);
const deletionId = ref(null);
const dialogAction = ref(null);

const task = reactive({
  ...props.initialTask,
  due_date: props.initialTask.due_date
    ? new Date(props.initialTask.due_date)
    : null,
  task_documents: props.initialTask.task_documents ? [...props.initialTask.task_documents] : []
});

const errors = ref(null);

const title = props.initialTask.isNew
  ? I18n.t('tasks.new_task')
  : I18n.t('tasks.edit_task');
const saveButtonText = props.initialTask.isNew
  ? I18n.t('tasks.create')
  : I18n.t('tasks.update_task');

const allLabels = computed(() => {
  let labels = [...existingLabels.value];

  if (props.issue) {
    props.issue?.tasks
      ?.map((task) => task.labels)
      .flat()
      .forEach((label) => {
        if (!labels.includes(label)) labels.push(label);
      });
  }
  return labels;
});

const taskLabelUrl = computed(() => {
  if (route.params.shaftId) {
    return `/organisations/${route.params.organisationId}/shafts_v2/shafts/${route.params.shaftId}/task_labels`;
  } else if (route.params.shipId) {
    return `/organisations/${route.params.organisationId}/ships/${route.params.shipId}/task_labels`;
  } else if (route.params.equipmentId) {
    return `/organisations/${route.params.organisationId}/equipment/${route.params.equipmentId}/task_labels`;
  } else {
    return `/organisations/${route.params.organisationId}/operations/${route.params.operationId}/plants/task_labels`;
  }
});

watch(() => props.errorSaving, (value) => {
  value !== null && (saving.value = !value);
});

onMounted(async () => {
  loadTaskLabels();
  modalCanScroll();
});

watch(task, modalCanScroll);

async function modalCanScroll() {
  modalOverflow.value = false;
  await nextTick();
  if (modalLayout.value?.clientHeight >= window.innerHeight)
    return (modalOverflow.value = true);
}

function cancel() {
  emit('cancel');
  dismissDialogAction();
}

function save() {
  saving.value = true;

  if (isValid()) {
    saving.value = true;
    // Show the notification only for existing issues as they are the slow ones
    props.issue?.id && attachmentNotification();
    // If the task is for a new issue and is being edited
    if (props.errorSaving === false) cancel();
    return emit('submit', { ...task });
  }

  saving.value = false;
}

function isValid() {
  let e = {};
  if (!task['description']) e.description = ['required'];
  if (!task['task_type']) e.task_type = ['required'];
  if (Object.keys(e).length > 0) {
    errors.value = { error_count: Object.keys(e).length, task: e };
    return false;
  } else {
    errors.value = null;
    return true;
  }
}

function attachmentNotification() {
  if (task?.task_documents) {
    // If we are saving a task and it has new attachments we will show the message
    const taskHasNewAttachments = task.task_documents?.some(attachment => attachment?.isNew);
    taskHasNewAttachments && emitter.emit('toast', { status: 'warning', message: I18n.t('issues.saving_task') });
  }
}

async function loadTaskLabels() {
  let { ok, data } = await ajax(taskLabelUrl.value);

  if (ok) {
    existingLabels.value = data;
  }
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  deletionId.value = null;
  dialogAction.value = null;
}

async function removeTaskAttachment(id) {
  if (dialogAction.value === 'delete') {
    task.task_documents = task.task_documents.filter(attachment => attachment.id !== id);
    emit('delete', { ...task });
    dismissDialogAction();
  }
}

function saveBlob(file) {
  new DirectUpload(file, uploadUrl);
}

function clearValidationMessage(key) {
  if (errors?.value?.task) {
    errors.value.task[key] = null;
    errors.value.error_count -= 1;
  }
}
</script>
